import React, { useState, useEffect } from 'react';
// Common Components (Corrected Paths)
import AppPaginator from '../../AppPaginator';
import AppSearchBox from '../../AppSearchBox';
import AppBreadCrumb from '../../common/AppBreadCrumb';
import PerPageBlock from '../../common/PerPageBlock';
import AppToaster from '../../AppToaster';
// API & Utils (Corrected Paths)
import { getRateTypes, fetchRateType, destroyRateType, destroyRateTypesMultiple } from '../../../api/RateTypesAPI';
import { handleApiError } from '../../../api/risapi';
// Report component if needed
// import RateTypesReport from './RateTypesReport';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

const RateTypesPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const successMessage = location.state?.message;

    const [entityName, setEntityName] = useState('Rate Type');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [reportLoading, setReportLoading] = useState(false);
    const [viewDataLoading, setViewDataLoading] = useState(false);
    const [searchTermCompleted, setSearchTermCompleted] = useState('');
    const [perPage, setPerPage] = useState(process.env.REACT_APP_PER_PAGE_COUNT || 10);
    const [enableMultipleItemSelect, setEnableMultipleItemSelect] = useState(false);
    const [selectedRateTypeIds, setSelectedRateTypeIds] = useState(new Set());

    const [rateTypesList, setRateTypesList] = useState({
        paginate: true, data: [], per_page: perPage, current_page: 1, tot_page: null,
    });

    const [toaster, setToaster] = useState({});
    const [showToaster, setShowToaster] = useState(false);
    const [saveCompleted, setSaveCompleted] = useState(false);

    // --- Navigation ---
    const handleAddClick = () => navigate('/franchisee_user/masters/rate-types/create');
    const handleEditClick = (id) => navigate(`/franchisee_user/masters/rate-types/edit/${id}`);

    // --- Delete Logic ---
    const handleDeleteClick = (id) => {
        fetchRateType(id, function (result) {
            if (result.rate_type) {
                const deleteItem = result.rate_type;
                Swal.fire({
                    title: 'Are you sure?',
                    html: `<p>Delete Rate Type: <strong>${deleteItem.name}</strong> (ID: ${deleteItem.id})?</p><p>This action cannot be undone.</p>`,
                    icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', cancelButtonColor: '#6c757d', confirmButtonText: 'Yes, delete it!'
                }).then((dialogResult) => {
                    if (dialogResult.isConfirmed) {
                        destroyRateType(deleteItem, function (apiResponse) {
                            if (apiResponse.rate_type) {
                                Swal.fire('Deleted!', 'Record deleted.', 'success');
                                setSaveCompleted(prev => !prev);
                            } else {
                                Swal.fire('Error!', apiResponse.error?.response?.data?.message || 'Could not delete.', 'error');
                            }
                        });
                    }
                });
            } else { Swal.fire('Error!', 'Could not fetch details.', 'error'); }
        });
    };

    // --- Multi-Delete Logic ---
    const showMultipleDeleteConfirmation = () => {
        const deletingItems = rateTypesList.data.filter(item => selectedRateTypeIds.has(item.id));
        if (deletingItems.length === 0) {
            Swal.fire('No Selection', 'Please select at least one Rate Type to delete.', 'info');
            return;
        }
        Swal.fire({
            title: `Delete ${deletingItems.length} Rate Types?`,
            html: `<p>You are about to delete the following items:</p><div style="max-height: 150px; overflow-y: auto;">${deletingItems.map(item => `<p>- ${item.name} (ID: ${item.id})</p>`).join('')}</div><p class="mt-2 text-danger">This action cannot be undone.</p>`,
            icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', cancelButtonColor: '#6c757d', confirmButtonText: 'Yes, delete them!'
        }).then((result) => {
            if (result.isConfirmed) {
                const idsToDelete = Array.from(selectedRateTypeIds);
                destroyRateTypesMultiple(idsToDelete, function (response) {
                    if (response.rate_type) {
                        Swal.fire('Deleted!', 'Selected records deleted.', 'success');
                        setEnableMultipleItemSelect(false);
                        setSelectedRateTypeIds(new Set());
                        setSaveCompleted(prev => !prev);
                    } else {
                        Swal.fire('Error!', response.error?.response?.data?.message || 'Could not delete selected items.', 'error');
                    }
                });
            }
        });
    };

    // --- Checkbox Handler ---
    const handleCheckboxChange = (id, isChecked) => {
        setSelectedRateTypeIds(prev => {
            const newSet = new Set(prev);
            if (isChecked) newSet.add(id); else newSet.delete(id);
            return newSet;
        });
    };

    // --- Effects ---
    useEffect(() => {
        if (successMessage) {
            setShowToaster(true);
            setToaster({ type: 'success', heading: 'Success', message: successMessage });
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [successMessage, location.pathname, navigate]);

    useEffect(() => {
        document.title = 'Manage Rate Types';
        let filters = { paginate: true, page: currentPage, per_page: perPage, search_term: searchTermCompleted };

        setViewDataLoading(true);
        setSelectedRateTypeIds(new Set());
        setEnableMultipleItemSelect(false);

        getRateTypes(filters, function (response) {
            setViewDataLoading(false);
            if (response.rate_types) {
                let data = []; let currentP = 1; let lastP = 1; let responsePP = perPage;
                if (Array.isArray(response.rate_types)) { data = response.rate_types; }
                else if (response.rate_types.data && Array.isArray(response.rate_types.data)) {
                    data = response.rate_types.data; currentP = response.rate_types.current_page; lastP = response.rate_types.last_page; responsePP = response.rate_types.per_page;
                } else { console.error("Unexpected API structure:", response.rate_types); handleApiError(response); return; }
                setRateTypesList({ paginate: filters.paginate, per_page: responsePP, data: data, current_page: currentP, tot_page: lastP });
            } else {
                handleApiError(response);
                setToaster({ type: 'danger', heading: 'Error', message: 'Could not load Rate Types.' });
                setShowToaster(true);
                setRateTypesList(prev => ({ ...prev, data: [], current_page: 1, tot_page: 1 }));
            }
        });
    }, [currentPage, searchTermCompleted, saveCompleted, perPage]);

    return (
        <>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <main>
                <div className="container-fluid">
                    <AppBreadCrumb title='Rate Types List'>
                        <li><a className="f-s-14 f-w-500" href="#">Masters</a></li>
                        <li className="active"><a className="f-s-14 f-w-500" href="#">Rate Types</a></li>
                    </AppBreadCrumb>

                    <div className="row bg-white">
                        <div className="col-12">
                            <div className="tab-wrapper mb-3">
                                <ul className="tabs">
                                    <li className="tab-link active" data-tab={1}>All Rate Types</li>
                                    <li className="ms-md-auto">
                                        <div className="text-md-end d-flex flex-column flex-md-row mt-md-3">
                                            <AppSearchBox searchTerm={searchTerm} setSearchTerm={setSearchTerm} setSearchTermCompleted={setSearchTermCompleted} />
                                            <button className="text-nowrap btn btn-red btn-sm ms-md-3 fw-semibold f-s-14" type="button" onClick={handleAddClick}>
                                                <i className="ph-bold  ph-circles-three-plus"></i> Add Rate Type
                                            </button>
                                            {/* Add RateTypesReport component here */}
                                        </div>
                                    </li>
                                </ul>
                            </div>
                            <div className="content-wrapper">
                                <div className="d-flex flex-wrap m-md-3">
                                    {rateTypesList.data.length > 0 ? (
                                        <>
                                            <AppPaginator entityName={entityName} currentPage={rateTypesList.current_page} totalPages={rateTypesList.tot_page} setCurrentPage={setCurrentPage} />
                                            <PerPageBlock perPage={perPage} setPerPage={setPerPage} />
                                            <div className='d-flex align-items-center ms-auto mt-2 mt-md-0'>
                                                <button className="btn btn-info btn-sm me-2" type="button" onClick={() => setEnableMultipleItemSelect(prev => !prev)}>
                                                    {enableMultipleItemSelect ? (<><i className="fa-solid fa-check fa-fw me-1" />Done</>) : 'Select'}
                                                </button>
                                                {enableMultipleItemSelect && selectedRateTypeIds.size > 0 && (
                                                    <button className="btn btn-danger btn-sm" type="button" onClick={showMultipleDeleteConfirmation}> Delete ({selectedRateTypeIds.size}) Records </button>
                                                )}
                                            </div>
                                        </>
                                    ) : ( !viewDataLoading && <p className="text-center w-100">No Rate Types found.</p> )}
                                    {(reportLoading || viewDataLoading) && <div className="loader_box ms-auto"><div className="loader_14"></div></div>}
                                </div>

                                {/* --- Card Layout Start --- */}
                                <div className="tabs-content active" id="tab-1">
                                    <div className="row">
                                        {!viewDataLoading && rateTypesList.data.map((rateType) => (
                                            <div className="col-md-6 col-xl-4 project-card" key={rateType.id}>
                                                <div className="card hover-effect">
                                                    <div className="card-header">
                                                        <div className="d-flex align-items-center">
                                                            {enableMultipleItemSelect && (
                                                                <input
                                                                    className="form-check-input me-2"
                                                                    type="checkbox"
                                                                    checked={selectedRateTypeIds.has(rateType.id)}
                                                                    onChange={(e) => handleCheckboxChange(rateType.id, e.target.checked)}
                                                                    id={`select-ratetype-${rateType.id}`}
                                                                />
                                                            )}
                                                            <div className="h-40 w-40 d-flex-center b-r-50 overflow-hidden me-2 btn-light-reddit">
                                                                 <i className="ti ti-tag f-s-18"></i> {/* Tag icon for Rate Type */}
                                                            </div>
                                                            <div className="flex-grow-1 ps-2">
                                                                <h6 className="m-0 text-dark f-w-600">{rateType.name}</h6>
                                                                {/* You can add more info here if RateType model gets more fields */}
                                                            </div>
                                                            <div className="dropdown">
                                                                <button aria-expanded="false" className="bg-none border-0" data-bs-toggle="dropdown" type="button"><i className="ti ti-dots-vertical text-dark" /></button>
                                                                <ul className="dropdown-menu dropdown-menu-end">
                                                                    <li><a className="dropdown-item" onClick={() => handleEditClick(rateType.id)}><i className="ti ti-edit text-success me-1" /> Edit</a></li>
                                                                    <li>
                                                                        <button className="dropdown-item delete-button" onClick={() => handleDeleteClick(rateType.id)}>
                                                                            <i className="ti ti-trash text-danger me-1" /> Delete
                                                                        </button>
                                                                    </li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {/* Optional Card Body */}
                                                    <div className="card-body py-2"> {/* Reduced padding */}
                                                         <div className="d-flex justify-content-end">
                                                              <small className="text-muted">ID: {rateType.id}</small>
                                                         </div>
                                                     </div>
                                                </div>
                                            </div>
                                        ))}
                                        {/* No Data Card */}
                                        {!viewDataLoading && rateTypesList.data.length === 0 && (
                                            <div className="col-12"> <div className="card mb-3"><div className="row g-0"><div className="col-md-12"><div className="card-body"><h5 className="card-title">No Rate Types Found</h5><p className="card-text">No records match your criteria or none have been added yet.</p></div></div></div></div></div>
                                        )}
                                    </div>
                                </div>
                                {/* --- Card Layout End --- */}

                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default RateTypesPage;